import { t } from 'i18next';
import { useMemo } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useBoolean } from 'src/hooks/use-boolean';

import { isMobile } from 'src/utils/user-agent';

import { authApi } from 'src/services/auth/auth.service';
import { AuthContext } from 'src/services/auth/auth.types';
import { Product } from 'src/services/billing/billing.types';
import { getActiveProduct } from 'src/services/billing/billing.utils';
import { getOrgTenant, Business_Tenants, isOrganisationTenant } from 'src/services/auth/auth.utils';
import {
  useGetProductsQuery,
  useGetMySubscriptionQuery,
} from 'src/services/billing/billing.services';
import {
  useGetMyAccountProfileQuery,
  useUpdateOnboardingContextMutation,
} from 'src/services/account/account.service';

import Label from 'src/components/label';

import ConfirmSubscriptionDialog from 'src/sections/admin/billing/payment/payment-confirm-subscription-dialog';

// ----------------------------------------------------------------------

export default function NavUpgrade() {
  const authResult = authApi.endpoints.getAuthContext.useQueryState();
  const { data: authData } = authResult;
  const { user } = authData as AuthContext;

  const router = useRouter();

  const [updateOnboardingContext] = useUpdateOnboardingContextMutation();

  const { currentData: activeSubscription, isError } = useGetMySubscriptionQuery();

  const { currentData: availableProducts } = useGetProductsQuery();

  const { currentData: profileData } = useGetMyAccountProfileQuery();

  const tenant = getOrgTenant(authData as AuthContext);

  const tenantProducts = useMemo(() => {
    if (!availableProducts) return;

    // eslint-disable-next-line consistent-return
    return availableProducts.filter((product) => product.tenant_type === tenant);
  }, [availableProducts, tenant]);

  const currentProduct = useMemo<Product | undefined>(() => {
    if (!tenantProducts) return;

    if (!activeSubscription || isError) {
      // Free Tier
      // eslint-disable-next-line consistent-return
      return tenantProducts.find((product) => !product.subscription_required);
    }

    const matchedProduct = getActiveProduct(activeSubscription, tenantProducts);

    // eslint-disable-next-line consistent-return
    return matchedProduct;
  }, [activeSubscription, tenantProducts, isError]);

  const showUpgradeModal = useBoolean();

  const showUpgradeLabels = Business_Tenants.includes(tenant);

  const onTakeTourClicked = async () => {
    try {
      await updateOnboardingContext({
        signup_flow: false,
      }).unwrap();

      router.push(paths.dashboard.root);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Stack
      sx={{
        px: 2,
        py: 5,
        textAlign: 'center',
      }}
    >
      <Stack alignItems="center">
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={profileData?.profile_image?.public_path}
            alt={user?.name}
            sx={{ width: 48, height: 48 }}
          >
            {user?.name?.charAt(0).toUpperCase()}
          </Avatar>

          {showUpgradeLabels && !currentProduct?.subscription_required && (
            <Label
              color="success"
              variant="filled"
              sx={{
                top: -6,
                px: 0.5,
                left: 40,
                height: 20,
                position: 'absolute',
                borderBottomLeftRadius: 2,
              }}
            >
              {t('common.free')}
            </Label>
          )}
        </Box>

        <Stack spacing={0.5} sx={{ mb: 2, mt: 1.5, width: 1 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.name}
          </Typography>

          <Typography variant="body2" noWrap sx={{ color: 'text.disabled' }}>
            {user?.email}
          </Typography>
        </Stack>

        {showUpgradeLabels && !currentProduct?.subscription_required && (
          <Button variant="contained" rel="noopener" onClick={() => showUpgradeModal.onTrue()}>
            {t('billing.subscription.upgrade')}
          </Button>
        )}

        {!isMobile() && !isOrganisationTenant(tenant) && (
          <Button variant="contained" onClick={onTakeTourClicked}>
            {t('joyride.take_tour')}
          </Button>
        )}
      </Stack>

      <ConfirmSubscriptionDialog
        chosenProduct={currentProduct}
        open={showUpgradeModal.value}
        onClose={showUpgradeModal.onFalse}
      />
    </Stack>
  );
}
